import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n";
import { CONTACT } from "../lib/contact";
import { useGoogleReviews } from "../lib/useGoogleReviews";

const Testimonials = () => {
  const { t, lang } = useI18n();
  const gr = useGoogleReviews();
  const [i, setI] = useState(0);

  const items = gr?.reviews?.length
    ? gr.reviews.map((r) => ({
        q: r.text,
        n: r.name,
        r: r.relativeTime,
        photo: r.photo,
        profileUri: r.profileUri,
        rating: r.rating || 5,
        reviewUri: r.reviewUri,
      }))
    : t.testimonials.items.map((it) => ({ ...it, rating: 5 }));

  useEffect(() => {
    setI(0);
  }, [lang, items.length]);

  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % items.length), 7000);
    return () => clearInterval(id);
  }, [items.length]);

  const next = () => setI((p) => (p + 1) % items.length);
  const prev = () => setI((p) => (p - 1 + items.length) % items.length);
  const item = items[Math.min(i, items.length - 1)];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-[#A60056] font-semibold mb-4">
            {lang === "pt" ? "Testemunhos" : "Reviews"}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white text-balance mb-4">
            {t.testimonials.title}
          </h2>
          <a
            href={gr?.mapsUri || CONTACT.gmaps}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#A60056]/10 border border-[#A60056]/25 text-[#A60056] text-xs font-medium hover:bg-[#A60056]/20 transition-colors"
            data-testid="reviews-badge"
          >
            <Star className="w-3.5 h-3.5 fill-[#A60056]" />
            {gr
              ? `${lang === "pt" ? gr.rating.toFixed(1).replace(".", ",") : gr.rating.toFixed(1)} ${lang === "pt" ? "no Google" : "on Google"} · ${gr.total} ${lang === "pt" ? "avaliações" : "reviews"}`
              : t.testimonials.badge}
          </a>
        </motion.div>

        <div className="relative">
          <div className="relative min-h-[400px] md:min-h-[340px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${lang}-${i}-${items.length}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="glass-strong rounded-3xl p-8 md:p-14 text-center"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="w-10 h-10 text-[#A60056] mx-auto mb-6 opacity-80" />
                <div className="flex justify-center gap-1 mb-6">
                  {["s1", "s2", "s3", "s4", "s5"].map((sk, k) => (
                    <Star
                      key={sk}
                      className={`w-4 h-4 ${k < item.rating ? "fill-[#A60056] text-[#A60056]" : "text-white/20"}`}
                    />
                  ))}
                </div>
                <p
                  className="text-base md:text-xl text-white/90 leading-relaxed mb-8 max-w-3xl mx-auto text-balance"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  "{item.q}"
                </p>
                <div className="flex items-center justify-center gap-3">
                  {item.photo ? (
                    <img
                      src={item.photo}
                      alt={item.n}
                      width="44"
                      height="44"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      className="w-11 h-11 rounded-full border border-white/20 object-cover"
                      data-testid="testimonial-avatar"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#A60056]/20 border border-[#A60056]/40 flex items-center justify-center text-[#A60056] font-semibold">
                      {(item.n || "?").charAt(0)}
                    </div>
                  )}
                  <div className="text-left">
                    {item.profileUri ? (
                      <a href={item.profileUri} target="_blank" rel="noreferrer" className="text-white font-medium hover:underline">
                        {item.n}
                      </a>
                    ) : (
                      <div className="text-white font-medium">{item.n}</div>
                    )}
                    <div className="text-sm text-white/45">
                      {item.reviewUri ? (
                        <a href={item.reviewUri} target="_blank" rel="noreferrer" className="hover:underline">
                          {item.r} · Google
                        </a>
                      ) : (
                        item.r
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prev}
              className="w-10 h-10 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              data-testid="testimonial-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1.5 flex-wrap justify-center max-w-xs">
              {items.map((it, k) => (
                <button
                  key={`${lang}-${it.n}-${k}`}
                  onClick={() => setI(k)}
                  data-testid={`testimonial-dot-${k}`}
                  className={`h-1.5 rounded-full transition-all ${
                    k === i ? "w-8 bg-[#A60056]" : "w-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                  aria-label={`Slide ${k + 1}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="w-10 h-10 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              data-testid="testimonial-next"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="text-center mt-8 space-y-2">
            <a
              href={gr?.mapsUri || CONTACT.gmaps}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors underline-offset-4 hover:underline"
              data-testid="reviews-see-more"
            >
              {t.testimonials.seeMore}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            {gr?.reviews?.length ? (
              <p className="text-[11px] text-white/25">
                {lang === "pt"
                  ? "Avaliações do Google, ordenadas por relevância."
                  : "Google reviews, ordered by relevance."}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
